/* eslint-disable react/prop-types */
import React from 'react'
import classnames from 'classnames'
import {
  lazyStartIndex,
  lazyEndIndex,
  getPreClones,
} from './utils/innerSliderUtils'

// given specifications/props for a slide, fetch all the classes that need to be applied to the slide
const getSlideClasses = (spec) => {
  let slickActive
  let slickCenter
  let centerOffset
  const index = spec.rtl ? spec.slideCount - 1 - spec.index : spec.index

  const slickCloned = index < 0 || index >= spec.slideCount
  if (spec.centerMode) {
    centerOffset = Math.floor(spec.slidesToShow / 2)
    slickCenter = (index - spec.currentSlide) % spec.slideCount === 0
    if (
      index > spec.currentSlide - centerOffset - 1 &&
      index <= spec.currentSlide + centerOffset
    ) {
      slickActive = true
    }
  } else {
    slickActive =
      spec.currentSlide <= index &&
      index < spec.currentSlide + spec.slidesToShow
  }

  let focusedSlide
  if (spec.targetSlide < 0) {
    focusedSlide = spec.targetSlide + spec.slideCount
  } else if (spec.targetSlide >= spec.slideCount) {
    focusedSlide = spec.targetSlide - spec.slideCount
  } else {
    focusedSlide = spec.targetSlide
  }
  const slickCurrent = index === focusedSlide
  return {
    'slick-slide': true,
    'slick-active': slickActive,
    'slick-center': slickCenter,
    'slick-cloned': slickCloned,
    'slick-current': slickCurrent, // dubious in case of RTL
  }
}

const getSlideStyle = (spec) => {
  const style = {}

  if (spec.variableWidth === undefined || spec.variableWidth === false) {
    style.width = spec.slideWidth
  }

  if (spec.fade) {
    style.position = 'relative'
    if (spec.vertical) {
      style.top = -spec.index * parseInt(spec.slideHeight, 10)
    } else {
      style.left = -spec.index * parseInt(spec.slideWidth, 10)
    }
    style.opacity = spec.currentSlide === spec.index ? 1 : 0
    if (spec.useCSS) {
      style.transition =
        `opacity ${spec.speed}ms ${spec.cssEase}, ` +
        `visibility ${spec.speed}ms ${spec.cssEase}`
    }
  }

  return style
}

const getKey = (child, fallbackKey) => child.key || fallbackKey

const renderSlides = (spec) => {
  let key
  const slides = []
  const preCloneSlides = []
  const postCloneSlides = []
  const childrenCount = React.Children.count(spec.children)
  const startIndex = lazyStartIndex(spec)
  const endIndex = lazyEndIndex(spec)

  React.Children.forEach(spec.children, (elem, index) => {
    let child
    const childOnClickOptions = {
      message: 'children',
      index,
      slidesToScroll: spec.slidesToScroll,
      currentSlide: spec.currentSlide,
    }

    // in case of lazyLoad, whether or not we want to fetch the slide
    if (
      !spec.lazyLoad ||
      (spec.lazyLoad && spec.lazyLoadedList.indexOf(index) >= 0)
    ) {
      child = elem
    } else {
      child = <div />
    }
    const childStyle = getSlideStyle({ ...spec, index })
    const slideClass = child.props.className || ''
    let slideClasses = getSlideClasses({ ...spec, index })
    const slidesAccessibility = {
      ...(spec.showAccesibilityRole && {
        role: 'group',
        'aria-label': `slide ${index + 1}`,
      }),
    }

    // push a cloned element of the desired slide
    slides.push(
      React.cloneElement(child, {
        key: `original${getKey(child, index)}`,
        'data-index': index,
        ...slidesAccessibility,
        className: classnames(slideClasses, slideClass),
        style: {
          ...(child.props.style || {}),
          ...childStyle,
        },
        onClick: (e) => {
          if (child.props && child.props.onClick) child.props.onClick(e)
          if (spec.focusOnSelect) {
            spec.focusOnSelect(childOnClickOptions)
          }
        },
      }),
    )

    // if slide needs to be precloned or postcloned
    if (spec.infinite && spec.fade === false) {
      const preCloneNo = childrenCount - index
      if (
        preCloneNo <= getPreClones(spec) &&
        childrenCount !== spec.slidesToShow
      ) {
        key = -preCloneNo
        if (key >= startIndex) {
          child = elem
        }
        slideClasses = getSlideClasses({ ...spec, index: key })
        preCloneSlides.push(
          React.cloneElement(child, {
            key: `precloned${getKey(child, key)}`,
            'data-index': key,
            className: classnames(slideClasses, slideClass),
            style: {
              ...(child.props.style || {}),
              ...childStyle,
            },
            onClick: (e) => {
              if (child.props && child.props.onClick) child.props.onClick(e)
              if (spec.focusOnSelect) {
                spec.focusOnSelect(childOnClickOptions)
              }
            },
          }),
        )
      }

      if (childrenCount !== spec.slidesToShow) {
        key = childrenCount + index
        if (key < endIndex) {
          child = elem
        }
        slideClasses = getSlideClasses({ ...spec, index: key })
        postCloneSlides.push(
          React.cloneElement(child, {
            key: `postcloned${getKey(child, key)}`,
            'data-index': key,
            className: classnames(slideClasses, slideClass),
            style: {
              ...(child.props.style || {}),
              ...childStyle,
            },
            onClick: (e) => {
              if (child.props && child.props.onClick) child.props.onClick(e)
              if (spec.focusOnSelect) {
                spec.focusOnSelect(childOnClickOptions)
              }
            },
          }),
        )
      }
    }
  })

  if (spec.rtl) {
    return preCloneSlides.concat(slides, postCloneSlides).reverse()
  } else {
    return preCloneSlides.concat(slides, postCloneSlides)
  }
}

export class Track extends React.PureComponent {
  node = null

  handleRef = (ref) => {
    this.node = ref
  }

  render() {
    const slides = renderSlides(this.props)
    const { onMouseEnter, onMouseOver, onMouseLeave } = this.props
    const mouseEvents = { onMouseEnter, onMouseOver, onMouseLeave }

    return (
      <div
        ref={this.handleRef}
        className='slick-track'
        style={this.props.trackStyle}
        {...mouseEvents}
      >
        {slides}
      </div>
    )
  }
}
